import { FRAME_RATE } from "../core/constants.js";

const SCRIPT_LINE_RE = /^(\d+):(\d+)\s+(press|rel)\s+([a-z]+)$/i;

export const BRUTEFORCE_ACTIONS = ["left", "right", "up"];

const DISABLED_ACTIONS = new Set(["down"]);

export function isBruteforceAction(action) {
  return BRUTEFORCE_ACTIONS.includes(action);
}

export function parseScriptFrameToken(value) {
  const trimmed = String(value ?? "").trim();
  const match = trimmed.match(/^(\d+):(\d+)$/);
  if (!match) {
    throw new Error(`Invalid time: ${trimmed || "(empty)"}`);
  }
  const seconds = Number(match[1]);
  const subFrame = Number(match[2]);
  if (!Number.isInteger(seconds) || !Number.isInteger(subFrame) || subFrame < 0 || subFrame >= FRAME_RATE) {
    throw new Error(`Invalid time: ${trimmed}`);
  }
  return seconds * FRAME_RATE + subFrame;
}

export function parseOptionalMaxFrameToken(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || trimmed === "0") {
    return null;
  }
  return parseScriptFrameToken(trimmed);
}

export function formatScriptFrame(frame) {
  const safeFrame = Math.max(0, Math.floor(frame));
  const seconds = Math.floor(safeFrame / FRAME_RATE);
  const subFrame = safeFrame % FRAME_RATE;
  return `${seconds}:${subFrame}`;
}

export function parseScriptText(text) {
  const events = [];
  const lines = String(text ?? "").split(/\r?\n/);
  let order = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    const match = trimmed.match(SCRIPT_LINE_RE);
    if (!match) {
      continue;
    }
    const seconds = Number(match[1]);
    const subFrame = Number(match[2]);
    const type = match[3].toLowerCase();
    const action = match[4].toLowerCase();
    if (!Number.isInteger(seconds) || !Number.isInteger(subFrame) || subFrame < 0 || subFrame >= FRAME_RATE) {
      continue;
    }
    if (DISABLED_ACTIONS.has(action)) {
      continue;
    }
    events.push({
      frame: seconds * FRAME_RATE + subFrame,
      type,
      action,
      order: order++,
    });
  }
  return events;
}

export function serializeScriptEvents(events) {
  return [...events]
    .sort((left, right) => {
      if (left.frame !== right.frame) {
        return left.frame - right.frame;
      }
      return (left.order ?? 0) - (right.order ?? 0);
    })
    .map((event) => `${formatScriptFrame(event.frame)} ${event.type} ${event.action}`)
    .join("\n");
}

export function normalizeScriptEvents(events) {
  return [...events]
    .filter((event) => !DISABLED_ACTIONS.has(event.action))
    .map((event, index) => ({
      frame: Math.max(0, Math.floor(event.frame)),
      type: event.type,
      action: event.action,
      order: event.order ?? index,
    }))
    .sort((left, right) => {
      if (left.frame !== right.frame) {
        return left.frame - right.frame;
      }
      return left.order - right.order;
    })
    .map((event, index) => ({ ...event, order: index }));
}

export function canonicalizeBruteforceEvents(events) {
  const normalized = normalizeScriptEvents(events);
  const actionState = new Map(BRUTEFORCE_ACTIONS.map((action) => [action, false]));
  const canonical = [];

  let index = 0;
  while (index < normalized.length) {
    const frame = normalized[index].frame;
    const frameEvents = [];
    while (index < normalized.length && normalized[index].frame === frame) {
      frameEvents.push(normalized[index]);
      index += 1;
    }

    const actionSummaries = new Map();
    for (const event of frameEvents) {
      if (!isBruteforceAction(event.action)) {
        canonical.push(event);
        continue;
      }
      if (!actionSummaries.has(event.action)) {
        actionSummaries.set(event.action, {
          firstOrder: event.order,
          nextState: actionState.get(event.action) === true,
        });
      }
      actionSummaries.get(event.action).nextState = event.type === "press";
    }

    const sortedSummaries = [...actionSummaries.entries()]
      .sort((left, right) => left[1].firstOrder - right[1].firstOrder);

    for (const [action, summary] of sortedSummaries) {
      const previousState = actionState.get(action) === true;
      if (summary.nextState === previousState) {
        continue;
      }
      canonical.push({
        frame,
        type: summary.nextState ? "press" : "rel",
        action,
        order: summary.firstOrder,
      });
      actionState.set(action, summary.nextState);
    }
  }

  return normalizeScriptEvents(canonical);
}
